import * as React from "react"
import { IoIosRibbon } from "react-icons/io"
import { gql } from "@apollo/client"
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import Head from "next/head"
import { useRouter } from "next/router"
import Lightbox from "yet-another-react-lightbox"
import Download from "yet-another-react-lightbox/plugins/download"
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen"
import Zoom from "yet-another-react-lightbox/plugins/zoom"
import "yet-another-react-lightbox/styles.css"

import { CompetitionPersonFragment, CompetitionPrizeFragment, useGetCompetitionQuery } from "lib/graphql"
import { transformImage } from "lib/helpers/utils"
import { categoryOptions } from "lib/static/options"
import { CompetitionApplicants } from "components/CompetitionApplicants"
import { CompetitionInfo } from "components/CompetitionInfo"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { Modal } from "components/Modal"
import { PartnersSlider } from "components/PartnersSlider"
import { SanitizedContent } from "components/SanitizedContent"

const _ = gql`
  fragment CompetitionPerson on CompetitionPerson {
    id
    name
    bio
    imageUrl
    type
    category
  }

  fragment CompetitionPartner on CompetitionPartner {
    id
    imageUrl
    url
    type
  }

  fragment CompetitionPrize on CompetitionPrize {
    id
    description
    position
    category
  }

  fragment CompetitionDetail on Competition {
    id
    name
    time
    content
    slug
    ticketLink
    shopLink
    program
    isFinal
    location
    disabilityProvisions
    deadline
    ...CompetitionDeadlines
    competitionPeople {
      ...CompetitionPerson
    }
    competitionPrizes {
      ...CompetitionPrize
    }
    competitionPartners {
      ...CompetitionPartner
    }
    applicants(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
      items {
        ...CompetitionApplicant
      }
      count
    }
  }

  query GetCompetition(
    $slug: String!
    $take: Int
    $orderBy: [ActOrderByWithRelationInput!]
    $where: ActWhereInput
    $skip: Int
  ) {
    competition(slug: $slug) {
      ...CompetitionDetail
    }
  }
`

export default function CompetitionDetail() {
  const [showAll, setShowAll] = React.useState(false)
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false)

  const router = useRouter()

  const isMobile = useBreakpointValue({ base: true, md: false })
  const colorBlackWhite = useColorModeValue("black", "white")
  const slug = router.query.slug as string

  const { data, loading } = useGetCompetitionQuery({
    skip: !slug,
    variables: { slug },
  })

  const competition = data?.competition

  const groupedPrizes = React.useMemo(
    () =>
      competition?.competitionPrizes?.reduce((acc, prize) => {
        const category = prize.category
        const position = prize.position
        if (acc[category]) {
          if (acc[category][position]) {
            acc[category][position].push(prize)
          } else {
            acc[category] = { ...acc[category], [position]: [prize] }
          }
        } else {
          acc[category] = { [position]: [prize] }
        }
        return acc
      }, {} as { [category: string]: { [position: number]: [CompetitionPrizeFragment] } }),
    [competition?.competitionPrizes],
  )

  if (loading)
    return (
      <Center h="100vh" w="100vw">
        <Spinner />
      </Center>
    )
  if (!competition) return null

  return (
    <>
      <Head>
        <title>Kunstbende - Wedstrijd</title>
      </Head>
      <Limiter mt={24} mb={56}>
        <Stack spacing={8}>
          <Flex justify="space-between" align="flex-end">
            <Heading
              fontSize={{ base: 32, md: 60 }}
              fontWeight={900}
              lineHeight={{ base: "shorter", md: "short" }}
              px={0}
              textAlign="left"
              color={colorBlackWhite}
            >
              {competition.name}
            </Heading>
          </Flex>
          {isMobile && (
            <Stack flex={3} spacing={10}>
              <CompetitionInfo competition={competition} />
            </Stack>
          )}

          <HStack spacing={32} alignItems="flex-start" position="relative">
            <Stack flex={5} spacing={8} position="relative">
              <SanitizedContent content={competition.content} />
              {competition.program && (
                <Stack spacing={0}>
                  <Heading fontSize="4xl" color="white" textAlign="left">
                    Programma
                  </Heading>

                  <Image
                    alt="programma"
                    src={competition.program}
                    pt={8}
                    onClick={() => setIsLightboxOpen(true)}
                    cursor="pointer"
                    aria-label="programma"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        setIsLightboxOpen(true)
                      }
                    }}
                  />

                  <Lightbox
                    open={isLightboxOpen}
                    close={() => setIsLightboxOpen(false)}
                    slides={[{ src: competition.program }]}
                    plugins={[Download, Fullscreen, Zoom]}
                    styles={{
                      container: {
                        outline: "none",
                      },
                      button: {
                        outline: "2px solid transparent",
                      },
                      root: {
                        "--yarl__focus-outline": "2px solid var(--chakra-colors-blue-400)",
                      },
                    }}
                  />
                </Stack>
              )}
            </Stack>
            {!isMobile && (
              <Stack flex={3} spacing={10} position="sticky" top={32}>
                <CompetitionInfo competition={competition} />
              </Stack>
            )}
          </HStack>
          <Stack spacing={24}>
            <CompetitionApplicants
              applicants={competition.applicants.items}
              isFinal={competition.isFinal}
              count={competition.applicants.items.flatMap((applicant) => applicant.members).length}
            />

            {competition.competitionPeople.filter((person) => person.type === "JURY").length > 0 && (
              <>
                <Stack spacing={8} pt={8}>
                  <Heading fontWeight="black" fontSize="5xl" pb={6}>
                    JURY
                  </Heading>
                  <Accordion allowToggle defaultIndex={[0]}>
                    {Object.keys(categoryOptions).map((category) => {
                      if (
                        competition.competitionPeople.filter(
                          (person) => person.type === "JURY" && person.category === category,
                        ).length > 0
                      ) {
                        return (
                          <AccordionItem key={category}>
                            <Stack spacing={4}>
                              <AccordionButton>
                                <Flex w="100%" justify="space-between" align="center">
                                  <Heading fontSize="2xl" color="yellow.500">
                                    {category}
                                  </Heading>
                                  <AccordionIcon />
                                </Flex>
                              </AccordionButton>
                              <AccordionPanel>
                                <SimpleGrid
                                  columns={{ base: 3, sm: 5, md: 6 }}
                                  spacing={{ base: 2, md: 16 }}
                                  minH="150px"
                                  w="full"
                                >
                                  {competition.competitionPeople
                                    .filter(
                                      (person) => person.type === "JURY" && person.category === category,
                                    )
                                    .map((person) => (
                                      <Person key={person.id} person={person} />
                                    ))}
                                </SimpleGrid>
                              </AccordionPanel>
                            </Stack>
                          </AccordionItem>
                        )
                      }
                    })}
                  </Accordion>
                </Stack>
                <Divider />
              </>
            )}
            {competition.competitionPeople.filter((person) => person.type === "HOST").length > 0 && (
              <>
                <Stack spacing={8}>
                  <Heading fontWeight="black" fontSize="5xl" pb={6}>
                    HOSTS
                  </Heading>
                  <SimpleGrid columns={{ base: 3, sm: 5, md: 6 }} spacing={{ base: 2, md: 16 }} minH="150px" w="full">
                    {competition.competitionPeople
                      .filter((person) => person.type === "HOST")
                      .map((person) => (
                        <Person key={person.id} person={person} />
                      ))}
                  </SimpleGrid>
                </Stack>
                <Divider />
              </>
            )}
            {competition.competitionPrizes.length > 0 && (
              <>
                <Stack spacing={8} pt={8}>
                  <Heading fontWeight="black" fontSize="5xl" pb={6}>
                    PRIJZEN
                  </Heading>
                  {groupedPrizes &&
                    Object.entries(groupedPrizes).map(([category, position], i) => {
                      if (!showAll && i > 0) return null
                      return (
                        <Box key={category}>
                          <Flex m={0} w="100%" justifyContent="space-between" alignItems="flex-end">
                            <Text
                              fontWeight="black"
                              textTransform="uppercase"
                              fontSize="3xl"
                              color="yellow.500"
                              mb={4}
                            >
                              {category}
                            </Text>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={5} py={4}>
                            {Object.entries(position).map(([position, prizes]) => (
                              <HStack key={position} position="relative" align="flex-start">
                                <Box>
                                  <Box as={IoIosRibbon} color="yellow.500" size="80px" zIndex={3} aria-label="Prijzen lint" />
                                  <Text
                                    fontSize="md"
                                    fontWeight="extrabold"
                                    position="absolute"
                                    top="13px"
                                    left="36px"
                                    zIndex={1}
                                    aria-label={`Prijs positie ${position}`}
                                  >
                                    {position}
                                  </Text>
                                </Box>

                                <Stack spacing={0}>
                                  {prizes.map((prize, i) => (
                                    <>
                                      <Text
                                        key={prize.id}
                                        fontSize="md"
                                        p={2}
                                        background={i % 2 ? "gray.600" : "gray.700"}
                                      >
                                        {prize.description}
                                      </Text>
                                    </>
                                  ))}
                                </Stack>
                              </HStack>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )
                    })}
                  {groupedPrizes && Object.keys(groupedPrizes).length > 1 && !showAll && (
                    <Button variant="ghost" onClick={() => setShowAll(!showAll)} w="fit-content">
                      Zie alle categorieën...
                    </Button>
                  )}
                </Stack>
                <Divider />
              </>
            )}
            {competition.competitionPartners.filter((partner) => partner.type === "FINANCIER").length > 0 && (
              <>
                <Stack spacing={8} m={6} overflow="hidden">
                  <Heading fontWeight="black" fontSize="5xl" pb={6}>
                    FINANCIERS
                  </Heading>

                  <PartnersSlider
                    partners={competition.competitionPartners.filter(
                      (partner) => partner.type === "FINANCIER",
                    )}
                  />
                </Stack>
                <Divider />
              </>
            )}
            {competition.competitionPartners.filter((partner) => partner.type === "PARTNER").length > 0 && (
              <>
                <Stack spacing={8} m={6} overflow="hidden">
                  <Heading fontWeight="black" fontSize="5xl" pb={6}>
                    PARTNERS & SPONSORS
                  </Heading>

                  {competition.slug === "kunstbende-young-creators-festival-2026" ? (
                    <Image
                      src="/1080x1920_digitale_logowand_metparade.jpg"
                      alt="KFYC 2026 logo"
                      aria-label="KFYC 2026 logo"
                    />
                  ) : (
                    <PartnersSlider
                      partners={competition.competitionPartners.filter((partner) => partner.type === "PARTNER")}
                    />
                  )}
                </Stack>
                <Divider />
              </>
            )}
          </Stack>
        </Stack>
      </Limiter>
    </>
  )
}

CompetitionDetail.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>

interface PersonProps {
  person: CompetitionPersonFragment
}

function Person({ person }: PersonProps) {
  const modalProps = useDisclosure()

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      modalProps.onOpen()
    }
  }

  return (
    <>
      <Head>
        <title>Kunstbende - Wedstrijd</title>
      </Head>
      <Stack
        align="center"
        cursor="pointer"
        minW={0}
        onClick={modalProps.onOpen}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Open details voor ${person.name}`}
        w="full"
        _focus={{ outline: "2px solid", outlineColor: "yellow.500", outlineOffset: "2px" }}
      >
        <Box w="full" maxW="140px" aspectRatio={1} overflow="hidden" borderRadius="xl">
          <Image
            alt={`${person.name} - ${person.bio}`}
            aria-label={`Foto van ${person.name}`}
            src={transformImage(person.imageUrl, "h_450")}
            w="full"
            h="full"
            objectFit="cover"
            cursor="pointer"
          />
        </Box>
        <Text textAlign="center" fontSize="md" my={1} noOfLines={2} w="full" minW={0}>
          {person.bio}
        </Text>
      </Stack>
      <Modal {...modalProps}>
        <Stack align="center">
          <Image
            mt={8}
            borderRadius="xl"
            src={transformImage(person.imageUrl, "h_450")}
            w="220px"
            h="220px"
            alt={`${person.name} - ${person.bio}`}
            aria-label={`Foto van ${person.name}`}
            objectFit="cover"
          />
          <Text fontSize="2xl" fontWeight="extrabold" my={3}>
            {person.name}
          </Text>
          <Text fontSize="xl" my={3}>
            {person.bio}
          </Text>
        </Stack>
      </Modal>
    </>
  )
}
